import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, EMPTY, Observable, of } from "rxjs";
import { catchError, switchMap, tap } from "rxjs/operators";
import { UtilsService } from "../../auth/service/utils.service";
import { IInvoice } from "./interfaces/invoice.interface";
import { UserService } from "../../auth/service";
import { ProductsService } from "../products/products.service";
import { CustomerService } from "../customer/customer.service";
import { ListService } from "../../auth/service/list.service";
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { OrdersService } from "../../auth/service/orders.service";

@Injectable({
  providedIn: "root"
})
export class InvoiceService {

  public lists = new BehaviorSubject<any>([]);
  public toGenerate = new BehaviorSubject<IInvoice[]>([]);
  public archived = new BehaviorSubject<IInvoice[]>([]);

  constructor(
    private http: HttpClient,
    private utils: UtilsService,
    private _userService: UserService,
    private _productService: ProductsService,
    private _customerService: CustomerService,
    private _listService: ListService,
    private _formBuilder: FormBuilder,
    private _utilsService: UtilsService,
    private _ordersService: OrdersService
  ) {
    this.itemsForLists().then((results) => this.lists.next(results));
  }

  getToGenerate(): Observable<any> {
    return this.http.get("/invoice/to-generate")
      .pipe(
        tap((result: IInvoice[]) => {
          result = result.map((invoice: any) => {
            if (invoice.users && invoice.users.length > 0) {
              invoice.workday_report_completed = false;
              let usersWorkdayReportCompleted: number = invoice.products.length;
              invoice.users.forEach((user: any) => {
                if (user.report_status === 2) {
                  usersWorkdayReportCompleted += 1;
                }
              });
              invoice.workday_report_completed = usersWorkdayReportCompleted === invoice.users.length + invoice.products.length;
            } else {
              invoice.workday_report_completed = true;
            }

            invoice.estimate_profit = 0;
            for (let user of invoice.users) {
              user.estimate_profit = user.invoice_residue || user.estimate_profit;
              invoice.estimate_profit += user.estimate_profit;
            }
            for (let product of invoice.products) {
              product.estimate_profit = product.invoice_residue || product.estimate_profit;
              invoice.estimate_profit += product.estimate_profit;
            }

            invoice.can_generate = invoice.workday_report_completed && !!invoice.order_number;

            return { ...invoice, commission_name: invoice.commission.complete_name || `#${invoice.commission.protocol} - ${invoice.commission.client.name} - ${invoice.commission.name}` };
          });

          this.toGenerate.next(result);
        }),
        catchError((error: any) => {
          this.toGenerate.next([]);
          this.utils.showToastrSuccess("ERROR_LOADING_INVOICES_TO_GENERATE");
          return error;
        })
      );
  }

  getToSend(): Observable<any> {
    return this.http.get<any>("/invoice/list/created").pipe(
      tap((results: IInvoice[]) => {
        results.map((invoice: any) => {
          invoice.products_in_rows = this.formatTooltip(invoice.products_in_rows) || [];
          invoice.users_in_rows = this.formatTooltip(invoice.users_in_rows) || [];
        });
      })
    );
  }

  getSended(): Observable<any> {
    return this.http.get<any>("/invoice/list/sent").pipe(
      tap((results: IInvoice[]) => {
        results.map((invoice: any) => {
          invoice.products_in_rows = this.formatTooltip(invoice.products_in_rows) || [];
          invoice.users_in_rows = this.formatTooltip(invoice.users_in_rows) || [];
        });
      })
    );
  }

  getCompleted(year: number): Observable<any> {
    return this.http.get<any>(`/invoice/list/completed?year=${year}`).pipe(
      tap((results: IInvoice[]) => {
        results.map((invoice: any) => {
          invoice.products_in_rows = this.formatTooltip(invoice.products_in_rows) || [];
          invoice.users_in_rows = this.formatTooltip(invoice.users_in_rows) || [];
        });
      })
    );
  }

  getArchived(): Observable<any> {
    return this.http.get<any>("/invoice/list/archived")
      .pipe(
        tap((results: IInvoice[]) => {
          results = results.map((invoice: any) => {

            invoice.products_names = this.formatTooltip(invoice.products_names);
            invoice.users_names = this.formatTooltip(invoice.users_names);

            if (invoice.users && invoice.users.length > 0) {
              invoice.workday_report_completed = false;
              let usersWorkdayReportCompleted: number = invoice.products.length;
              invoice.users.forEach((user: any) => {
                if (user.report_status === 2) {
                  usersWorkdayReportCompleted += 1;
                }
              });
              invoice.workday_report_completed = usersWorkdayReportCompleted === invoice.users.length + invoice.products.length;
            } else {
              invoice.workday_report_completed = true;
            }

            invoice.estimate_profit = 0;
            for (let user of invoice.users) {
              user.estimate_profit = user.invoice_residue || user.estimate_profit;
              invoice.estimate_profit += user.estimate_profit;
            }
            for (let product of invoice.products) {
              product.estimate_profit = product.invoice_residue || product.estimate_profit;
              invoice.estimate_profit += product.estimate_profit;
            }
            invoice.commission_name = !!invoice.commission.complete_name ? invoice.commission.complete_name : `#${invoice.commission.protocol} - ${invoice.commission.client.name} - ${invoice.commission.name}`

            return invoice;
          });

          this.archived.next(results);

          return results
        }),
        catchError((error: any) => {
          this.toGenerate.next([]);
          this.utils.showToastrError("ERROR_LOADING_INVOICES_ARCHIVED");
          return error;
        })
      );
  }

  archive(data: { analytics_ids: number[]; commission_type: string; }): Observable<any> {
    return this.http.put<any>("/invoice/archive", data)
      .pipe(
        tap((result: any) => {
          if (result.error) {
            this.utils.showToastrError("ERROR_ARCHIVE_INVOICE");
          } else {
            const toGenerate = this.toGenerate.value.filter((invoice: IInvoice) => !data.analytics_ids.includes(invoice.analytics_id));
            this.toGenerate.next(toGenerate);
            this.utils.showToastrSuccess("SUCCESS_ARCHIVE_INVOICE");
          }
          return result;
        }),
        catchError(() => this.utils.showToastrError("ERROR_ARCHIVE_INVOICE"))
      );
  }

  unarchive(data: { analytics_ids: number[]; commission_type: string; }): Observable<any> {
    return this.http.put<any>("/invoice/unarchive", data)
      .pipe(
        tap((result: any) => {
          if (result.error) {
            this.utils.showToastrError("ERROR_UNARCHIVE_INVOICE");
          } else {
            const archived = this.archived.value.filter((invoice: IInvoice) => !data.analytics_ids.includes(invoice.analytics_id));
            this.archived.next(archived);
            this.utils.showToastrSuccess("SUCCESS_UNARCHIVE_INVOICE");
          }
          return result;
        }),
        catchError(() => this.utils.showToastrError("ERROR_UNARCHIVE_INVOICE"))
      );
  }

  insert(data: any) {
    let attachments: any;

    if (data.attachments) {
      attachments = data.attachments.map((a) => a);
      delete data.attachments;
    }

    return this.http.post("/invoice", data).pipe(
      tap((res: any) => {
        if (attachments.length > 0) {
          return this.uploadAttachments(attachments, res.id).subscribe();
        }
        this.getToGenerate().subscribe();
        return { error: false, res };
      }),
      catchError(() => this.utils.showToastrError("ERROR_INSERT_INVOICE"))
    );
  }

  update(id: number, data: any) {
    if (data.attachments && data.attachments.length > 0) {
      return this.uploadAttachments(data.attachments, id).pipe(switchMap(() => {
        delete data.attachments;
        return this.updateInvoice(id, data);
      }));
    }

    return this.updateInvoice(id, data);
  }

  private updateInvoice(id, data) {
    return this.http.put(`/invoice/${id}`, data).pipe(
      tap((res: { error: boolean; message: string }) => {
        if (res.error) {
          this.utils.showToastrError("ERROR_UPDATE_INVOICE");
        } else {
          this.utils.showToastrSuccess("SUCCESS_UPDATE_INVOICE");
        }
        return of(res);
      }),
      catchError((error) => {
        this.utils.showToastrError("ERROR_UPDATE_INVOICE");
        return of({ error: true, message: error });
      })
    );
  }

  uploadAttachments(files: any, id: number) {
    const formData: FormData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("files[]", files[i]);
    }
    return this.http.put(`/invoice/upload/${id}`, formData).pipe(
      catchError(() => this.utils.showToastrError("ERROR_UPLOAD_INVOICE_ATTACHMENT"))
    );
  }

  get(id: number): Observable<any> {
    return this.http.get(`/invoice/${id}`);
  }

  updateStatus(ids: number[], status: string) {
    return this.http.put(`/invoice/status/${status}`, { invoices: ids })
      .pipe(
        tap((res: { error: boolean; message: string }) => !res.error),
        catchError(async (error: any) => {
          return { error: true, message: error };
        })
      );
  }

  setPayed(id: number, date: string) {
    return this.http.put(`/invoice/${id}`, { cashed: true, cashed_date: date }).pipe(
      tap((res: { error: boolean; message: string }) => {
        if (res.error) {
          this.utils.showToastrError("ERROR_UPDATE_INVOICE");
        } else {
          this.utils.showToastrSuccess("SUCCESS_UPDATE_INVOICE");
        }
        return res;
      }),
      catchError(() => this.utils.showToastrError("ERROR_UPDATE_INVOICE"))
    );

  }

  syncPayed() {
    return this.http.get("/invoice/check-cashed-from-esolver")
  }

  deleteAttachment(attachment_id: number) {
    return this.http.delete(`/invoice/attachment/${attachment_id}`).pipe(
      tap((result: { error: boolean; message: string }) => {
        if (result.error) {
          this.utils.showToastrError("ERROR_DELETE_INVOICE_ATTACHMENT");
        } else {
          this.utils.showToastrSuccess("SUCCESS_DELETE_INVOICE_ATTACHMENT");
        }
        return result;
      }),
      catchError(() => this.utils.showToastrError("ERROR_DELETE_INVOICE_ATTACHMENT"))
    );
  }

  delete(id: number) {
    return this.http.delete(`/invoice/${id}`).pipe(
      tap((result: { error: boolean; message: string }) => {
        if (result.error) {
          this.utils.showToastrError("ERROR_DELETE_INVOICE");
        } else {
          this.utils.showToastrSuccess("SUCCESS_DELETE_INVOICE");
        }
        return result;
      }),
      catchError(() => this.utils.showToastrError("ERROR_DELETE_INVOICE"))
    );
  }

  async itemsForLists() {
    await this._userService.getAllUsers();
    const documents = await this._listService.getList("invoice_document_type");
    return {
      users: this._userService.allUserList,
      products: await this._productService.getProductsList(),
      customers: await this._customerService.getCustomers(),
      rows_types: await this._listService.getList("invoice_row_type"),
      payment_measure_units: await this._listService.getList("payment_measure_unit"),
      payment_terms: await this._listService.getList("payment_terms"),
      invoice_services: await this._listService.getList("invoice_service"),
      document_codes: await this._listService.getList("invoice_document_code"),
      invoice_document_types: documents.filter((item: any) => !item.other.credit_note),
      credit_note_types: documents.filter((item: any) => item.other.credit_note)
    };
  }

  buildForm(isBlank: boolean) {
    return this._formBuilder.group({
      invoice_type: new FormControl(null, [Validators.required]),
      document_date: new FormControl(null, [Validators.required]),
      document_code_id: new FormControl(null, [Validators.required]),
      client_id: new FormControl(null, [Validators.required]),
      cig: new FormControl(null, isBlank ? [] : [Validators.minLength(10), Validators.maxLength(10)]),
      cup: new FormControl(null, isBlank ? [] : [Validators.minLength(15), Validators.maxLength(15)]),
      vat_code: new FormControl("22", [Validators.required, Validators.minLength(1), Validators.maxLength(3)]),
      accrual_exercise: new FormControl(null, [Validators.required]),
      expiration_date: new FormControl(null),
      payment_terms: new FormControl(null, [Validators.required]),
      rows: this._formBuilder.array([]),
      total_amount: new FormControl(0, [Validators.required]),
      has_client_approval: new FormControl(false),
      order_id: new FormControl(null, isBlank ? [] : [Validators.required, Validators.min(1)]),
      order_product_id: new FormControl(null, isBlank ? [] : [Validators.required, Validators.min(1)]),
      order_child_id: new FormControl(null),
      order_number: new FormControl(null, isBlank ? [] : [Validators.required]),
      order_date: new FormControl(null),
      invoice_order_number: new FormControl(null, isBlank ? [] : [Validators.required, Validators.minLength(2)])
    });
  }

  buildRow(id: number, defaultRowType: number, defaultServiceTrack: number) {
    return this._formBuilder.group({
      id: new FormControl(id),
      row_type: new FormControl(defaultRowType, [Validators.required]),
      service_track: new FormControl(defaultServiceTrack, [Validators.maxLength(25)]),
      service_description: new FormControl("PRESTAZIONE NS.PERSONALE", [Validators.required, Validators.minLength(2), Validators.maxLength(200)]),
      description: new FormControl(null, [Validators.minLength(2), Validators.maxLength(500)]),
      quantity: new FormControl(null, [Validators.min(1)]),
      price: new FormControl(0),
      vat_code: new FormControl(null),
      payment_measure_unit: new FormControl(null, [Validators.required]),
      discount: new FormControl(null),
      user_hash: new FormControl(null),
      product_id: new FormControl(null),
      analytics_id: new FormControl(null),
      has_parent: new FormControl(false),
      total_amount: new FormControl(0, [Validators.required]),
      with_product: new FormControl(false),
      month_name: new FormControl(null),
      month: new FormControl(null),
      year: new FormControl(null),
      order_id: new FormControl(null),
      childs: new FormControl(null)
    });
  }

  buildMergedRow(id: number, defaultRowType: number, defaultServiceTrack: any, unit_id: number | null, childs: number[]) {
    return this._formBuilder.group({
      id: new FormControl(id),
      invoice_type: new FormControl("RIG"),
      row_type: new FormControl(defaultRowType, [Validators.required]),
      service_track: new FormControl(defaultServiceTrack.id, [Validators.maxLength(25)]),
      service_description: new FormControl("PRESTAZIONE NS.PERSONALE", [Validators.required, Validators.minLength(2), Validators.maxLength(200)]),
      description: new FormControl(null, [Validators.minLength(2), Validators.maxLength(500)]),
      quantity: new FormControl(1, [Validators.min(1)]),
      price: new FormControl(0, [Validators.min(1)]),
      vat_code: new FormControl(defaultServiceTrack.other.sales_vat),
      payment_measure_unit: new FormControl(unit_id, [Validators.required]),
      discount: new FormControl(null),
      user_hash: new FormControl(null),
      product_id: new FormControl(null),
      analytics_id: new FormControl(null),
      has_parent: new FormControl(false),
      with_product: new FormControl(false),
      total_amount: new FormControl(0, [Validators.required]),
      childs: new FormControl(childs)
    });
  }

  fieldHasError(form: FormGroup, field: string, showErrors: boolean) {
    const f = form?.controls[field];
    if (showErrors) {
      return f?.invalid || f?.status === "INVALID" || (f?.value == null && f?.errors?.required);
    } else {
      return f?.invalid && f?.touched;
    }
  }

  rowFieldHasError(rows: FormArray, index: number, field: string, showErrors: boolean) {
    const f = rows?.controls[index]?.get(field);
    if (!f) return false;
    if (showErrors) {
      return f.invalid;
    } else {
      return f.invalid && f.touched;
    }
  }

  getAnalyticsId(row, month_name: string) {
    const data = {
      month: row.get("month").value,
      year: row.get("year").value,
      order_id: row.get("order_id").value,
      hash: row.get("user_hash").value,
      product_id: row.get("product_id").value
    };

    if (!data.year || !data.month || !data.order_id || (!data.product_id && !data.hash)) return EMPTY;

    return this.http.post("/commission/single/analytics", data)
      .pipe(
        tap(async (data: { analytics_id: number; estimate_profit: number; invoice_residue: number }) => {
          if (!!data) {
            await this._utilsService.showToastrSuccess("SUCCESS_LINK_TO_ECONOMIC_DETAIL");
            row.patchValue({ month_name, analytics_id: Number(data.analytics_id) });
          } else {
            await this._utilsService.showToastrError("UNABLE_LINK_TO_ECONOMIC_DETAIL");
          }
          return data;
        }),
        catchError(async (error: any) => {
          await this._utilsService.showToastrError("ERROR_LINK_TO_ECONOMIC_DETAIL");
          return error;
        })
      );
  }

  openLastRow(el: null | HTMLElement = null) {
    setTimeout(() => {
      const details = document.querySelectorAll("#invoice-rows .invoice-detail");
      details.forEach((detail) => detail.classList.remove("active"));
      if (el) {
        el.classList.add("active");
      } else {
        details[details.length - 1]?.classList.add("active");
      }
    }, 1);
  }

  async deleteAttachedFile(file: { id: number; url: string; filename: string }) {
    return this._utilsService
      .showSWAL({
        title: "CONFIRM_DELETE_FILE",
        icon: "error",
        confirmButtonText: "CONFIRM_DELETE_BTN",
        customClass: {
          confirmButton: "btn btn-danger"
        }
      })
      .then(async (result) => {
        if (result.value) {
          this._utilsService.showLoading();
          return this.deleteAttachment(file.id).subscribe(() => {
            this._utilsService.hideLoading();
            return true;
          });
        } else if (result.isDismissed) {
          await this._utilsService.showSWALError({
            title: "ABORTED_TITLE",
            text: "ABORTED_TEXT"
          });
          return false;
        }
      })
      .catch((error: any) => {
        console.error(error);
        return false;
      });
  }

  calculatePercentage(row: AbstractControl) {
    const discountAmount = (row.value.price * (row.value.discount || 0)) / 100;
    const newPrice = row.value.price - discountAmount;
    row.patchValue({ price: newPrice, total_amount: Number((newPrice * row.value.quantity).toFixed(2)) });
  }

  calculateTotalAmount(rows: any) {
    let total = 0;
    for (let row of rows.controls) {
      if (!row.value || row.value.has_parent || !row.value.total_amount) continue;
      total += Number(row.value.total_amount);
    }
    return total;
  }

  isOpenInvoiceDetail(el: HTMLElement) {
    return el.classList.contains("active");
  }

  explodeAdditionalData(additional_data: string): { IdDocumento: number, Data: string } {
    const output: any = {};
    if (!additional_data.length) return output;
    additional_data.split(";").forEach(part => {
      const [_, key, value] = part.match(/\[DatiOrdineAcquisto\|(\w+)\|([^\]]+)]/);
      output[key] = value === "null" ? null : value;
    });
    return output;
  }

  async manageResidue(rows, residual_count) {
    let totalMissingAmount: number = 0;

    const amounts: { analytics: number; total: number }[] = [];

    rows
      .filter((element: any) => !!element.analytics_id)
      .forEach((element: any) => {
        const a = amounts.find((a: any) => a.analytics === element.analytics_id);
        if (a) {
          a.total += element.total_amount;
        } else {
          amounts.push({ analytics: element.analytics_id, total: element.total_amount });
        }
      });

    amounts.forEach((a) => {
      const item = residual_count.find((r) => r.analytics === a.analytics);
      let row = rows.find((row: any) => row.analytics_id === a.analytics);
      row.invoice_residue = item.residual - a.total;
      totalMissingAmount += row.invoice_residue;
    });

    return totalMissingAmount;
  }

  formatTooltip(names: string[]) {
    return names.filter((item: string, index: number) => names.indexOf(item) === index);
  }

  handlerChangeDocumentType(invoiceForm, analytics) {
    const invoiceType: number = invoiceForm.value.invoice_type;
    const docType: any = this.lists.value.invoice_document_types.find((type: any) => type.id === invoiceType);
    const defaultVatCode: number = Number(analytics?.invoice_type.vat || 22);
    const vat_code = docType ? (docType.other.vat.toString() || docType.other.iva) : defaultVatCode;
    invoiceForm.patchValue({ vat_code });
  }

  ifHaveOneOrder(selectableOrders, invoiceForm) {
    if (selectableOrders.length === 1) {
      const selectedOrder = selectableOrders[0];
      invoiceForm.patchValue({
        order_id: selectedOrder.is_product ? null : selectedOrder.id,
        order_product_id: selectedOrder.is_product ? selectedOrder.id : null,
        order_number: selectedOrder.order_number,
        order_date: selectedOrder.order_date,
        invoice_order_number: selectedOrder.order_number
      });
    }
  }

  async getClientOrders(invoiceForm: AbstractControl) {
    try {
      this._utilsService.showLoading()
      const ordersList: any = await this._ordersService.listForClient(invoiceForm.value.client_id);
      let orders = [];

      ordersList.map((order: any) => {
        const o = {
          id: order.order_id || order.order_product_id,
          order_number: order.order_number,
          parent: null,
          description: order.order_description,
          order_date: order.order_date,
          is_product: order.is_product
        };

        orders.push(o);

        for (const child of order.orders_child) {
          orders.push({
            id: child.order_id || child.order_product_id,
            order_number: child.order_number,
            parent: child.id,
            description: child.order_description,
            order_date: child.order_date,
            is_product: child.is_product
          });
        }
      });

      if(orders.length) {
        invoiceForm.patchValue({
          order_id: null,
          order_product_id: null,
          order_child_id: null,
          order_number: null,
          order_date: null,
          invoice_order_number: null,
        })
      }
      this._utilsService.hideLoading()

      return orders;
    } catch (error) {
      this._utilsService.hideLoading()
      console.error(error);
      return [];
    }
  }
}